import { MultiSelectField } from '@ui/components/ui/multi-select';
import { ApprovalRecipient } from '@data';
// Straight from the hook module rather than through the `@data` barrel, so a test can replace it.
import { useApprovalRecipients } from '@data/hooks/sprintProducts';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapseIn } from '@app/components/core/collapse-in';
import { cn } from '@ui/lib/utils';
import {
  ApprovalRecipientsField,
  type ApprovalRecipientsFieldHandle,
  type FormOptionType,
} from './ApprovalRecipientsField';

export type DefaultRecipients = {
  colleagues: ApprovalRecipient[];
  colleagueEmails: Set<string>;
  /** Who the dialog opens with: the managers first, fixed, then the colleagues. */
  initial: FormOptionType[];
  /** Managers, then colleagues, then anything typed in by hand. */
  inOrder: (list: FormOptionType[]) => FormOptionType[];
  isPending: boolean;
};

/**
 * Who a decision on this report reaches unless told otherwise. Asked of the server because the two
 * groups come from different places, and shared by both dialogs: approving and disputing go to the
 * same people.
 */
export const useDefaultRecipients = (sprintProductId?: string): DefaultRecipients => {
  const { data: defaults, isPending } = useApprovalRecipients(sprintProductId);

  // Keyed on the answer alone. Naming anything here with `t` would put it in the dependency list, and
  // `t` is not promised a stable identity — a new one rebuilds this, which rebuilds the form's initial
  // values, which resets the form, which renders again. The list arrives empty every time.
  const colleagues = useMemo(() => defaults?.colleagues ?? [], [defaults]);
  const managers = useMemo(() => defaults?.managers ?? [], [defaults]);

  const colleagueEmails = useMemo(() => new Set(colleagues.map(c => c.email.toLowerCase())), [colleagues]);

  // Managers lead because they are the part of the list that does not move: take every colleague off and
  // the list still reads from the same place rather than reshuffling around whoever is left.
  const inOrder = useCallback(
    (list: FormOptionType[]): FormOptionType[] => [
      ...list.filter(r => r.isFixed),
      ...list.filter(r => !r.isFixed && colleagueEmails.has(r.value.toLowerCase())),
      ...list.filter(r => !r.isFixed && !colleagueEmails.has(r.value.toLowerCase())),
    ],
    [colleagueEmails],
  );

  // Managers always receive a copy, so their chips are fixed and the picker never offers them: there is
  // nothing to choose. Colleagues are the only ones that can come off, and the only ones to put back.
  const initial = useMemo(
    () => [
      ...managers.map(m => ({ value: m.email, label: m.email, isFixed: true })),
      ...colleagues.map(c => ({ value: c.email, label: c.email, isFixed: false })),
    ],
    [colleagues, managers],
  );

  return {
    colleagues,
    colleagueEmails,
    initial,
    inOrder,
    // A query with nothing to ask for reports itself as pending forever, so waiting on that alone would
    // leave the dialog disabled for good on a report whose id never arrived.
    isPending: !!sprintProductId && isPending,
  };
};

export type RecipientsSectionProps = {
  recipients: DefaultRecipients;
  value: FormOptionType[];
  onChange: (next: FormOptionType[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  error?: unknown;
  onDraftInvalid: () => void;
};

/**
 * The "To" line of a decision: who it goes to, and the way back for a colleague taken off it. Both the
 * approve and the dispute dialog render this, so the two are the same control rather than two
 * look-alikes that drift.
 */
export const RecipientsSection = React.forwardRef<ApprovalRecipientsFieldHandle, RecipientsSectionProps>(
  (
    { recipients: { colleagues, colleagueEmails, inOrder }, value, onChange, onBlur, disabled, error, onDraftInvalid },
    ref,
  ): ReactElement => {
    const { t } = useTranslation();

    const chosenEmails = useMemo(() => new Set(value.map(r => r.value.toLowerCase())), [value]);

    // The picker is only worth its space once it has something to offer. It stays out of the way while
    // every colleague is still on the list, and slides in the moment one is taken off so there is
    // somewhere to put them back — the only thing removing a name would otherwise be hard to undo.
    const colleagueRemoved = colleagues.some(c => !chosenEmails.has(c.email.toLowerCase()));

    // The list is held open by this rather than by itself, because ticking the last colleague back takes
    // the field it belongs to off screen, and a list left open then floats over the dialog with nothing
    // underneath it. It closes without its fade (see contentClassName below), so the field can go at the
    // same moment rather than waiting for an animation to finish over the top of it.
    const [pickerOpen, setPickerOpen] = useState(false);
    useEffect(() => {
      if (!colleagueRemoved) setPickerOpen(false);
    }, [colleagueRemoved]);

    // The picker owns the two default groups; anything free-typed is left alone by it.
    const selectedColleagues = value.filter(r => colleagueEmails.has(r.value.toLowerCase())).map(r => r.value);

    const onColleaguesChange = (nextEmails: string[]) => {
      // Everything the picker does not speak for — the fixed managers, and anything typed in by hand —
      // is carried across untouched.
      const rest = value.filter(r => !colleagueEmails.has(r.value.toLowerCase()));
      const chosen = nextEmails.map(email => ({ value: email, label: email, isFixed: false }));
      // Reordered rather than appended, so putting a colleague back does not land them after an address
      // that was typed in later.
      onChange(inOrder([...rest, ...chosen]));
    };

    return (
      // No gap on the column: a flex gap is kept even when the child it separates has collapsed to
      // nothing, which left a strip of empty dialog above the field at all times and made the picker
      // look like it was uncovering something. The spacing lives inside the collapsing part instead, so
      // it arrives and leaves with it.
      <div className="flex flex-col">
        {/* Nothing to click on once it has collapsed, whatever is still mounted inside it. */}
        <CollapseIn show={colleagueRemoved} className={cn(!colleagueRemoved && 'pointer-events-none')}>
          <div
            className={cn(
              // Height is what makes room; the control itself settles into it rather than being wiped
              // into view, which is what read as cheap.
              'pb-4 transition-transform duration-300 ease-out',
              colleagueRemoved ? 'translate-y-0' : '-translate-y-1',
            )}
          >
            <MultiSelectField
              className="sm:max-w-sm"
              label={t('approvalAddColleaguesBack')}
              options={colleagues.map(c => ({
                value: c.email,
                label: c.name,
                description: c.name === c.email ? undefined : c.email,
              }))}
              value={selectedColleagues}
              onChange={onColleaguesChange}
              emptyLabel={t('selectColleagues')}
              searchPlaceholder={t('searchColleagues')}
              emptySearchLabel={t('approvalColleaguesNone')}
              selectAllLabel={t('selectAll')}
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              // No fade on the way out: the field it belongs to leaves at the same moment, and a list
              // dissolving over that space is what washed the brand colour across it — its ticked boxes
              // are filled with that colour.
              contentClassName="data-closed:animate-none"
              searchable={colleagues.length > 6}
              disabled={disabled}
            />
          </div>
        </CollapseIn>

        <ApprovalRecipientsField
          ref={ref}
          label={t('to')}
          placeholder="name@example.com"
          invalidEmailMessage={t('invalidEmailDraft')}
          fixedHint={t('approvalManagerAlwaysCopied')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          error={error}
          onDraftInvalid={onDraftInvalid}
        />
      </div>
    );
  },
);
RecipientsSection.displayName = 'RecipientsSection';
